/* AURA consent reconciliation (dev, not published).
   Goal: when a visitor already made a valid choice in the AURA banner but Shopify's
   Customer Privacy API has no decision on record, send that same choice to Shopify.
   Rules:
   - Never creates consent: it only re-sends the visitor's own AURA choice (refusals included).
   - Never overrides a decision Shopify already holds (yes or no). If it contradicts the AURA
     choice, the AURA choice is cleared and the banner is shown again so the visitor decides.
   - A missing, malformed or expired AURA choice (> 365 days) is not used: the banner is shown again.
   Debug: add ?aura_consent_debug=1 to the URL, state is also exposed in window.AuraConsent. */
(function(){
  'use strict';

  var MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
  var PURPOSES = ['preferences', 'analytics', 'marketing'];

  var banner = document.querySelector('[data-aura-cookie]');
  if (!banner) return;

  var version = banner.dataset.consentVersion || '1';
  var storageKey = 'aura:consent:v' + version;
  var cookieKey = 'aura_consent_v' + version;
  var debug = /[?&]aura_consent_debug=1/.test(window.location.search);
  var state = window.AuraConsent = { version: version, action: 'pending' };

  function log(){
    if (!debug || !window.console) return;
    try { console.info.apply(console, ['[AURA consent]'].concat(Array.prototype.slice.call(arguments))); } catch (e) {}
  }

  function readCookie(name){
    var prefix = name + '=';
    var parts = document.cookie ? document.cookie.split(';') : [];
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (part.indexOf(prefix) === 0) {
        try { return decodeURIComponent(part.slice(prefix.length)); } catch (e) { return null; }
      }
    }
    return null;
  }

  function readChoice(){
    var raw = null;
    try { raw = window.localStorage && window.localStorage.getItem(storageKey); } catch (e) {}
    if (!raw) raw = readCookie(cookieKey);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return { invalid: true }; }
  }

  function isValid(choice){
    if (!choice || choice.invalid || !choice.values || typeof choice.values !== 'object') return false;
    for (var i = 0; i < PURPOSES.length; i++) {
      if (typeof choice.values[PURPOSES[i]] !== 'boolean') return false;
    }
    if (typeof choice.decidedAt !== 'number') return false;
    var age = Date.now() - choice.decidedAt;
    return age >= -60000 && age < MAX_AGE_MS;
  }

  function clearChoice(){
    try { window.localStorage && window.localStorage.removeItem(storageKey); } catch (e) {}
    document.cookie = cookieKey + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
  }

  function showBanner(){
    banner.hidden = false;
    window.requestAnimationFrame(function(){ banner.classList.add('is-visible'); });
  }

  function withPrivacyApi(callback){
    var api = function(){ return (window.Shopify && window.Shopify.customerPrivacy) || null; };
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function(error){
        callback(error ? null : api(), error || null);
      });
    } else {
      callback(api(), null);
    }
  }

  function finish(action, extra){
    state.action = action;
    if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) state[k] = extra[k];
    log(action, state);
    try { document.dispatchEvent(new CustomEvent('aura:consent:reconciled', { detail: state })); } catch (e) {}
  }

  var choice = readChoice();

  if (!choice) {
    finish('no-aura-choice');          /* aura-launch.js shows the banner */
    return;
  }

  if (!isValid(choice)) {
    clearChoice();
    showBanner();
    finish('aura-choice-expired-or-invalid');
    return;
  }

  state.aura = choice.values;
  state.decidedAt = choice.decidedAt;

  withPrivacyApi(function(api, loadError){
    if (!api || typeof api.currentVisitorConsent !== 'function' || typeof api.setTrackingConsent !== 'function') {
      finish('privacy-api-unavailable', { error: loadError ? String(loadError.message || loadError) : null });
      return;
    }

    var current = api.currentVisitorConsent() || {};
    state.shopifyBefore = current;
    state.region = typeof api.getRegion === 'function' ? api.getRegion() : null;
    state.bannerRequired = typeof api.shouldShowBanner === 'function' ? api.shouldShowBanner() : null;

    var shopifyUndecided = !current.marketing && !current.analytics && !current.preferences;

    if (!shopifyUndecided) {
      var mismatch = PURPOSES.filter(function(p){
        var s = current[p];
        return s && (s === 'yes') !== choice.values[p];
      });
      if (mismatch.length) {
        /* Contradictory states: keep Shopify's decision untouched and let the visitor decide again. */
        clearChoice();
        showBanner();
        finish('conflict-banner-reopened', { mismatch: mismatch });
        return;
      }
      finish('shopify-decision-kept', { mismatch: mismatch });
      return;
    }

    api.setTrackingConsent({
      preferences: choice.values.preferences,
      analytics: choice.values.analytics,
      marketing: choice.values.marketing
    }, function(error){
      if (error) {
        finish('sync-error', { error: String(error.message || error) });
        return;
      }
      finish('synced-from-aura-choice', { shopifyAfter: api.currentVisitorConsent() });
    });
  });
})();
